import { createRouter, createWebHistory } from 'vue-router'

import { KratosFlowType } from '@/enums/kratos'
import { browserNavigate, whoami } from '@/libs/auth'
import { kratosFlowBrowserUrl } from '@/libs/kratos'

/**
 * The ID portal's screens and the Kratos UI URLs of `kratos.yml`: login `/login`,
 * settings `/settings`, recovery (the invite) `/invite`, error `/error`. `Blank` for every
 * unauthenticated screen, `Page` for `/settings`, the one screen behind a session.
 *
 * `/` goes to `/settings`, not `/login`: it is Kratos's `default_browser_return_url`, where Kratos
 * sends a browser that already has a session, so pointing it at `/login` would loop.
 */
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path:     '/',
            redirect: '/settings',
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/Settings.vue'),
            meta:      { layout: 'Page', requiresAuth: true },
        },
        {
            path:      '/invite',
            name:      'invite',
            component: () => import('@/views/Invite.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/logout',
            name:      'logout',
            component: () => import('@/views/Logout.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/logout/confirm',
            name:      'logout-confirm',
            component: () => import('@/views/LogoutConfirm.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/error',
            name:      'error',
            component: () => import('@/views/Error.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
    ],
})

// Every route is PROTECTED unless it opts out with `requiresAuth: false` — a route that forgets
// its meta fails closed. Without a Kratos session the browser is sent to a new Kratos login flow
// whose `return_to` is the page that was asked for.
router.beforeEach(async (to) => {
    if (to.meta.requiresAuth === false) return true

    if (await whoami()) return true

    browserNavigate(kratosFlowBrowserUrl(KratosFlowType.Login, { return_to: `${window.location.origin}${to.fullPath}` }))
    return false
})

export default router
